import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  History,
  Wallet,
  Home,
  Settings,
  Trophy,
  Flame,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'main', name: 'Главная', icon: Home, description: 'Live ставки' },
  { id: 'history', name: 'История', icon: History, description: 'Мои ставки' },
  { id: 'settings', name: 'Профиль', icon: Settings, description: 'Настройки' }
];

export default function VulkanBetTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [combinedPages, setCombinedPages] = useState(false);
  const [browserTheme, setBrowserTheme] = useState('dark');
  const [addRandomOps, setAddRandomOps] = useState(true);
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  const previewRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      const profilesWithIds = response.data.map(profile => ({
        ...profile,
        visibleId: profile.visibleId || Math.floor(1000000 + Math.random() * 9000000)
      }));
      setProfiles(profilesWithIds);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const formatPhone = (phone) => {
    if (!phone) return "+7 (999) 123-45-67";
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
  };

  const parseD = (d, t) => {
    if (!d) return 0;
    const p = d.split('.');
    if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}T${t || '00:00'}`).getTime();
    return 0;
  };

  const fmtDate = (ts) => {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  };

  const generateBets = (transactions) => {
    const result = [];

    // INVERT: OZON deposit → VulkanBet withdrawal, OZON withdrawal → VulkanBet deposit
    const sorted = [...(transactions || [])].sort((a, b) => parseD(b.date, b.time) - parseD(a.date, a.time));
    
    for (const tx of sorted) {
      const isOzonDeposit = tx.type === 'deposit';
      result.push({
        type: isOzonDeposit ? 'withdrawal' : 'deposit',
        amount: Math.abs(parseFloat(tx.amount || 0)),
        date: tx.date,
        time: tx.time || `${String(Math.floor(Math.random()*14)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
      });
    }

    if (addRandomOps) {
      const dates = result.map(b => parseD(b.date, b.time)).filter(d => d > 0);
      const minT = dates.length > 0 ? Math.min(...dates) : Date.now() - 30*86400000;
      const maxT = dates.length > 0 ? Math.max(...dates) : Date.now();
      const count = Math.floor(Math.random() * 4) + 3; // 3-6

      for (let i = 0; i < count; i++) {
        const t = new Date(minT + Math.random() * (maxT - minT));
        const isDeposit = Math.random() > 0.45;
        result.push({
          type: isDeposit ? 'deposit' : 'withdrawal',
          amount: Math.floor(Math.random() * 12000) + 500,
          date: fmtDate(t.getTime()),
          time: `${String(Math.floor(Math.random()*14)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
        });
      }

      // First deposit (oldest) — always пополнение up to 6000 RUB
      const oldestDate = new Date(minT);
      oldestDate.setDate(oldestDate.getDate() - Math.floor(Math.random() * 5) - 2);
      result.push({
        type: 'deposit',
        amount: Math.floor(Math.random() * 4000) + 2000,
        date: fmtDate(oldestDate.getTime()),
        time: `${String(Math.floor(Math.random()*12)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
        isFirstDeposit: true,
      });
    }

    result.sort((a, b) => parseD(b.date, b.time) - parseD(a.date, a.time));
    return result;
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.accountName?.split(' ')[0] || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.accountName?.split(' ')[1] || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email && selectedProfile.email !== "не установлен" 
      ? selectedProfile.email 
      : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mail.ru`;
    
    // Use template_balance as the total balance, split into main + bonus
    const totalBalance = selectedProfile.template_balance || selectedProfile.balance || 50000;
    const bonusPercent = 0.1 + Math.random() * 0.05; // 10-15% bonus
    const mainBalance = Math.floor(totalBalance * (1 - bonusPercent));
    const bonusBalance = totalBalance - mainBalance;
    
    const bets = generateBets(selectedProfile.transactions || selectedProfile.operations);
    
    const data = {
      visibleId: selectedProfile.visibleId,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      phone: formatPhone(phone),
      email,
      balance: mainBalance.toString(),
      bonusBalance: bonusBalance.toString(),
      totalBalance: totalBalance.toString(),
      bets,
      browserTheme
    };
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    const dataStr = encodeURIComponent(JSON.stringify(generatedData));
    
    if (combinedPages) {
      return `${window.location.origin}/templates/vulkanbet/combined?data=${dataStr}&theme=${browserTheme}`;
    }
    
    return `${window.location.origin}/templates/vulkanbet/${selectedTemplate}?data=${dataStr}&theme=${browserTheme}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    try {
      const response = await axios.post(
        `${API}/templates/vulkanbet/archive`,
        { data: generatedData, pages: ['main', 'history', 'settings'] },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `VulkanBet_${generatedData.visibleId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Архив скачан!");
    } catch (error) {
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="vulkanbet-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <Flame className="w-5 h-5 text-white" />
            </div>
            VulkanBet Ставки
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор страницы букмекера из профиля OZON
          </p>
        </div>
        <Button 
          onClick={() => fetchProfiles()}
          variant="outline"
          data-testid="refresh-profiles-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Обновить профили
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Выберите шаблон</Label>
              <div className="grid grid-cols-3 gap-3">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setGeneratedData(null);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${
                        selectedTemplate === template.id ? 'text-orange-500' : 'text-slate-400'
                      }`} />
                      <div className={`font-medium ${
                        selectedTemplate === template.id ? 'text-orange-700' : 'text-slate-700'
                      }`}>
                        {template.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{template.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Combined Pages Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-orange-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={combinedPages}
                  onChange={(e) => {
                    setCombinedPages(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Объединить все страницы</div>
                  <div className="text-xs text-slate-500">
                    Все страницы в одной ссылке с навигацией через меню
                  </div>
                </div>
              </label>
            </div>

            {/* Browser Theme */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Тема браузера</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setBrowserTheme('light'); setGeneratedData(null); }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    browserTheme === 'light'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className={browserTheme === 'light' ? 'text-orange-700' : 'text-slate-700'}>☀️ Светлая</span>
                </button>
                <button
                  onClick={() => { setBrowserTheme('dark'); setGeneratedData(null); }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    browserTheme === 'dark'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className={browserTheme === 'dark' ? 'text-orange-700' : 'text-slate-700'}>🌙 Тёмная</span>
                </button>
              </div>
            </div>

            {/* Random Operations */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-orange-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={addRandomOps} onChange={(e) => { setAddRandomOps(e.target.checked); setGeneratedData(null); }}
                  className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                <div>
                  <div className="font-medium text-slate-700">Рандомные операции</div>
                  <div className="text-xs text-slate-500">3-6 случайных ввод/вывод + первое пополнение до 6000 р.</div>
                </div>
              </label>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Профиль OZON</Label>
              <div className="relative">
                <button
                  data-testid="profile-dropdown"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.accountName || selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">
                          ID: {selectedProfile.visibleId} • Баланс: {(selectedProfile.template_balance || selectedProfile.balance || 0).toLocaleString()} ₽
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {profiles.length === 0 ? (
                      <div className="px-4 py-3 text-slate-400 text-center">
                        Нет профилей
                      </div>
                    ) : (
                      profiles.map((profile) => (
                        <button
                          key={profile._id || profile.id}
                          onClick={() => {
                            setSelectedProfile(profile);
                            setDropdownOpen(false);
                            setGeneratedData(null);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                            selectedProfile?._id === profile._id || selectedProfile?.id === profile.id ? 'bg-orange-50' : ''
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.accountName || profile.name}</div>
                            <div className="text-xs text-slate-500">
                              ID: {profile.visibleId} • {(profile.template_balance || profile.balance || 0).toLocaleString()} ₽ • {(profile.transactions || profile.operations || []).length} операций
                            </div>
                          </div>
                          {(selectedProfile?._id === profile._id || selectedProfile?.id === profile.id) && (
                            <Check className="w-4 h-4 text-orange-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
              data-testid="generate-btn"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trophy className="w-4 h-4 mr-2" />
              )}
              Сгенерировать
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Preview */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр данных</h2>
            
            {generatedData ? (
              <div className="space-y-4">
                {/* Preview Info */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Имя:</span>
                    <span className="text-slate-800 font-medium">{generatedData.fullName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Телефон:</span>
                    <span className="text-slate-800">{generatedData.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Email:</span>
                    <span className="text-slate-800">{generatedData.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">ID:</span>
                    <span className="text-slate-800">{generatedData.visibleId}</span>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Балансы:</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-orange-600">Основной:</span> {parseFloat(generatedData.balance).toLocaleString()} ₽</div>
                      <div><span className="text-green-600">Бонусный:</span> {parseFloat(generatedData.bonusBalance).toLocaleString()} ₽</div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-600">Ставок:</span>
                    <span className="text-slate-800 ml-2">{generatedData.bets.length}</span>
                    <span className="text-green-600 ml-2">
                      (выигрышей: {generatedData.bets.filter(b => b.type === 'win').length})
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    onClick={openPreview}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="open-preview-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Открыть
                  </Button>
                  <Button 
                    onClick={copyLink}
                    variant="outline"
                    className="flex-1"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Копировать ссылку
                  </Button>
                </div>
                <Button onClick={downloadArchive} disabled={isDownloadingArchive} variant="outline" className="w-full" data-testid="download-archive-btn">
                  {isDownloadingArchive ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Скачать архив всех страниц
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Flame className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-slate-500">
                  Выберите профиль и нажмите «Сгенерировать»
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
